import { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-map-gl/mapbox";

import {
  MARKER_COLLISION_MAX_ZOOM,
  computeHiddenMarkerIds,
  type MarkerBox,
} from "./marker-collision";

/** Priority/selection metadata for one marker; the DOM node supplies geometry. */
export type CollisionMarker = {
  id: string;
  /** Lower value = higher priority (kept when markers overlap). */
  priority: number;
  /** Always visible and space-reserving (e.g. the selected station). */
  pinned: boolean;
};

type MarkerCollisionResult = {
  /** Ids of markers that overlap a higher-priority marker and should be hidden. */
  hiddenIds: Set<string>;
  /** Stable per-id ref callback to register a marker's measurable DOM element. */
  registerMarker: (id: string) => (element: HTMLElement | null) => void;
};

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

const EMPTY_HIDDEN_IDS: Set<string> = new Set();

/**
 * Hides lower-priority station markers that overlap higher-priority ones, and
 * shows everything again once the map is zoomed in past
 * {@link MARKER_COLLISION_MAX_ZOOM}. Measures live DOM boxes (markers stay
 * mounted, only their visibility is toggled) and recomputes on map pan/zoom and
 * whenever the marker set or its data changes.
 */
export function useMarkerCollision(markers: CollisionMarker[]): MarkerCollisionResult {
  const { current: map } = useMap();

  const elementsRef = useRef(new Map<string, HTMLElement>());
  const refCallbacksRef = useRef(new Map<string, (element: HTMLElement | null) => void>());
  const markersRef = useRef(markers);
  markersRef.current = markers;
  const frameRef = useRef<number | null>(null);

  const [hiddenIds, setHiddenIds] = useState<Set<string>>(EMPTY_HIDDEN_IDS);

  const registerMarker = useCallback((id: string) => {
    let callback = refCallbacksRef.current.get(id);
    if (!callback) {
      callback = (element: HTMLElement | null) => {
        if (element) elementsRef.current.set(id, element);
        else elementsRef.current.delete(id);
      };
      refCallbacksRef.current.set(id, callback);
    }
    return callback;
  }, []);

  const recompute = useCallback(() => {
    frameRef.current = null;
    if (!map) return;

    if (map.getZoom() >= MARKER_COLLISION_MAX_ZOOM) {
      setHiddenIds((prev) => (prev.size === 0 ? prev : EMPTY_HIDDEN_IDS));
      return;
    }

    const boxes: MarkerBox[] = [];
    for (const marker of markersRef.current) {
      const element = elementsRef.current.get(marker.id);
      if (!element) continue;
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue;
      boxes.push({
        id: marker.id,
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        priority: marker.priority,
        pinned: marker.pinned,
      });
    }

    const next = computeHiddenMarkerIds(boxes);
    setHiddenIds((prev) => (setsEqual(prev, next) ? prev : next));
  }, [map]);

  const schedule = useCallback(() => {
    if (frameRef.current != null) return;
    frameRef.current = requestAnimationFrame(recompute);
  }, [recompute]);

  // Recompute after every commit so marker set / size / data changes are picked
  // up. rAF-batched, and a no-op result keeps the same Set reference (React then
  // bails out of the re-render), so this does not loop.
  useEffect(() => {
    schedule();
  });

  useEffect(() => {
    if (!map) return;
    const handleViewportChange = () => schedule();
    map.on("move", handleViewportChange);
    map.on("zoom", handleViewportChange);
    map.on("resize", handleViewportChange);
    return () => {
      map.off("move", handleViewportChange);
      map.off("zoom", handleViewportChange);
      map.off("resize", handleViewportChange);
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [map, schedule]);

  return { hiddenIds, registerMarker };
}
